/* ProductFinder.jsx */
import React, { useState, useEffect, useReducer, useCallback, memo } from 'react';
import PropTypes from 'prop-types';

/* ------------------------------------------------ */
/* Utilities                                        */
/* ------------------------------------------------ */
const API_BASE = process.env.REACT_APP_PRODUCTFINDER_API ?? '/api/productfinder';
const rootEl = document.getElementById('productfinder-root');
const vorlage = rootEl.getAttribute('data-vorlage');

const fetchWithAbort = async (url, signal) => {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error('Network response was not ok');
    return res.json();
};

/* ------------------------------------------------ */
/* Reducer + State                                  */
/* ------------------------------------------------ */
const initialState = {
    answers:      {},      // { frageId: optionId }
    history:      [],      // Array<Question>
    question:     null,    // aktuelle Frage
    products:     [],      // Ergebnis Produkte
    loading:      true,
    error:        null,
    answered:     0,       // beantwortete Fragen
    remaining:    null,     // noch übrige Fragen
    headline:     ''
};

const scrollToRoot = () => {
    const el = document.getElementById('productfinder-root');
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
};

function reducer(state, action) {
    switch (action.type) {
        case 'RESET_ERROR':
            return { ...state, error: null };
        case 'FETCH_START':
            return { ...state, loading: true, error: null };
        case 'FETCH_QUESTION':
            const question = action.payload.frage;
            if(action.payload.headline) {
                state.headline = action.payload.headline;
            }
            if(action.payload.description) {
                state.description = action.payload.description;
            }
            if(action.payload.length === 0) {
                // Entferne das DOM-Element, wenn es keine Frage gibt
                const el = document.getElementById('productfinder-root');
                if (el) el.remove();
                return{
                    ...state,
                    loading: false,
                    question: null,
                    history: [...state.history, null],
                    answered: 0,
                    remaining: 0,
                    products: []
                };
            }
            return {
                ...state,
                loading: false,
                question: question,
                history: [...state.history, question],
                answered: question.answered,
                remaining: question.remaining,
                products: []
            };
        case 'FETCH_PRODUCTS':
            return { ...state, loading: false, products: action.payload, question: null };
        case 'SET_ANSWERS':
            return { ...state, answers: action.payload };
        case 'BACK':
            return {
                ...state,
                answers: action.answers,
                history: action.history,
                question: action.history[action.history.length - 1] ?? null,
                products: []
            };
        case 'ERROR':
            return { ...state, loading: false, error: action.payload };
        default:
            return state;
    }
}

/* ------------------------------------------------ */
/* UI-Sub-Komponenten                               */
/* ------------------------------------------------ */
const SelectionSummary = memo(({ history, answers, onJump }) => (
    <div className="mb-4">
        <h3 className={`mb-3`}>Bisherige Auswahl:</h3>
        <div className="row">
            {history.map((frage, idx) => {
                const optId = answers[frage.id];
                const selected = frage.optionen.find(o => o.id === optId);
                if (!selected) return null;
                if (optId === -1) {
                    return (
                        <div className="border rounded p-2 h-100 alert-warning shadow" key={frage.id}>
                            <strong>{idx + 1}. {frage.short}</strong><br />
                            <em>Keine Auswahl getroffen</em>
                        </div>
                    );
                }
                return (
                    <div className="col-md-4 mb-3" key={frage.id}>
                        <div
                            role="button"
                            className="border rounded p-2 h-100 alert-warning shadow"
                            title="Klicken, um diese Auswahl zu ändern"
                            onClick={() => onJump(frage.id)}
                        >
                            <div className="d-flex align-items-start justify-content-between">
                                <div>
                                    <strong>{idx + 1}. {frage.short}</strong>
                                    <br/>
                                    {selected.name}
                                </div>

                                <div className="">
                                {selected.image && (
                                    <img
                                        src={selected.image}
                                        alt={selected.name}
                                        loading="lazy"
                                        className="img-thumbnail mr-2"
                                        style={{ height: '3rem', objectFit: 'contain' }}
                                    />
                                )}
                                </div>

                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    </div>
));
SelectionSummary.propTypes = {
    history:  PropTypes.array.isRequired,
    answers:  PropTypes.object.isRequired,
    onJump:   PropTypes.func.isRequired
};

const QuestionView = memo(({ question, givenAnswer, onAnswer, questionNumber }) => (
    <>
        <h3 className={`mb-3`}>{question.answered ? question.answered + 1 : 1}. {question.label}</h3>
        <div className="row justify-content-center bg-light py-3" style={{ gap: '2rem' }}>
            {question.optionen.map(opt => {
                const selected = givenAnswer === opt.id;
                return (
                    <div
                        key={opt.id}
                        className={`card shadow col-sm-12 col-md-3 p-0 ${selected ? 'bg-light' : 'bg-white'}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => onAnswer(question.id, opt.id)}
                    >
                        {opt.image && (
                            <img loading="lazy" src={opt.image} alt={opt.name} className="card-img-top w-100" />
                        )}
                        <div className="card-header text-center font-weight-bold">
                            {selected && <span className="badge badge-light ml-2">✓</span>} {opt.name}
                        </div>
                        {opt.description && <div className="card-body" dangerouslySetInnerHTML={{ __html: opt.description }}></div>}
                    </div>
                );
            })}

            {question.optional === 1 && (
                <div
                    className={`card m-2 shadow col-sm-12 col-md-3 p-0 ${givenAnswer === -1 ? 'bg-light' : 'bg-white'}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onAnswer(question.id, -1)}
                >
                    <div className="card-header text-center font-weight-bold">
                        {givenAnswer === -1 && <span className="badge badge-light ml-2">✓</span>} Keine Auswahl
                    </div>
                    <div className="card-body">Diese Frage überspringen</div>
                </div>
            )}
        </div>
    </>
));
QuestionView.propTypes = {
    question:     PropTypes.object.isRequired,
    givenAnswer:  PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onAnswer:     PropTypes.func.isRequired
};

const ProductList = memo(({ products }) => (
    <>
        <h3 className={`mb-3`}>Produktvorschläge</h3>
        <div className="row">
            {products.map(p => (
                <div className="col-md-4 mb-4" key={p.products_id}>
                    <a href={p.url} className="text-decoration-none text-reset" target="_blank" rel="noopener noreferrer">
                        <div className="bg-white h-100 shadow productPreviewSpacer">
                            {p.image && <img loading="lazy" src={p.image} className="card-img-top" alt={p.name} />}
                            <div className="card-body">
                                <h5 className="card-title text-center" style={{minHeight: '3.5em'}} >{p.name}</h5>

                                <div className="mb-3 px-4 text-muted" style={{minHeight: '4em'}}>
                                    <div className="text-left">
                                        <div>
                                            {p.anzahlAmLager > 0 && <span><i className="fa fa-circle fa-fw text-success"
                                                                             aria-hidden="true"></i> {p.anzahlAmLager} am Lager</span>}
                                            {p.anzahlAmLager < 1 && <span><i className="fa fa-circle fa-fw text-grey"
                                                                             aria-hidden="true"></i> bestellbar</span>}
                                            {p.muster === 1 &&
                                            <div className="text-left"><i className="fa fa-tags fa-fw"
                                                                          aria-hidden="true"></i> Muster verfügbar
                                            </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <p className="card-text previewPriceFinal text-center">
                                    {new Intl.NumberFormat("de-DE", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }).format(p.price)}
                                    &nbsp;EUR
                                </p>
                            </div>
                        </div>
                    </a>
                </div>
            ))}
        </div>
    </>
));
ProductList.propTypes = {products: PropTypes.array.isRequired};

/* ------------------------------------------------ */
/* Haupt-Komponente                                 */
/* ------------------------------------------------ */
const ProductFinder = () => {
    const [state, dispatch] = useReducer(reducer, initialState);
    const [loadedFinder, setLoadedFinder] = useState(false);
    const [showFinder, setShowFinder] = useState(true);

    /* ---------- Erstes Laden ---------- */
    useEffect(() => {
        const controller = new AbortController();
        dispatch({ type: 'FETCH_START' });

        fetchWithAbort(`${API_BASE}/?vorlage=${vorlage}`, controller.signal)
            .then(data => {
                // Falls kein valider Inhalt enthalten ist → keine Aktion
                if (!data) {
                    // Entferne das DOM-Element, wenn es keine Frage gibt
                    const el = document.getElementById('productfinder-root');
                    if (el) el.remove();
                    return;
                }

                dispatch({
                    type: 'FETCH_QUESTION',
                    payload: data,
                    answered: 0
                });

                setLoadedFinder(true);
            })
            .catch(err => {
                if (err.name !== 'AbortError') {
                    dispatch({ type: 'ERROR', payload: 'Fehler beim Laden der ersten Frage.' });
                }
            });

        return () => controller.abort();
    }, []);

    /* ---------- Automatisch Single-Choice beantworten ---------- */
    useEffect(() => {
        // nur ausführen, wenn keine Anfrage mehr läuft
        if (!state.loading && state.question && state.question.optionen?.length === 1) {
            const onlyOption = state.question.optionen[0];

            handleAnswer(state.question.id, onlyOption.id, true);
        }
    }, [state.loading, state.question]);   // reagiert nur auf neue Fragen

    /* ---------- Handler ---------- */
    const loadNext = useCallback(async (newAnswers) => {
        const controller = new AbortController();
        dispatch({ type: 'FETCH_START' });
        try {
            const qs = new URLSearchParams(newAnswers).toString();
            const data = await fetchWithAbort(`${API_BASE}/?${qs}&vorlage=`+vorlage, controller.signal);

            if (window.dataLayer && typeof window.dataLayer.push === 'function') {
                window.dataLayer.push({
                    event: 'finderKlick',
                    frageId: data.frage?.id ?? null,
                    frageText: data.frage?.label ?? '',
                    vorlage: vorlage
                });
            }

            if (data.frage?.id) {
                dispatch({
                    type: 'FETCH_QUESTION',
                    payload: data
                });
            } else if (data.produkte) {
                dispatch({ type: 'FETCH_PRODUCTS', payload: data.produkte });
            }
        } catch {
            dispatch({ type: 'ERROR', payload: 'Fehler beim Laden der nächsten Frage.' });
        }
        return () => controller.abort();
    }, []);

    const handleAnswer = useCallback((frageId, optionId, auto = false) => {
        const newAnswers = { ...state.answers, [frageId]: optionId, auto };
        dispatch({ type: 'SET_ANSWERS', payload: newAnswers });
        scrollToRoot(); //scroll back to finder-top
        loadNext(newAnswers);
    }, [state.answers, loadNext]);

    const backOneStep = useCallback(() => {
        if (state.history.length < 1) return;
        const newHistory = state.history.slice(0, -1);
        const newAnswers = { ...state.answers };
        delete newAnswers[state.history[state.history.length - 1].id];
        dispatch({ type: 'BACK', history: newHistory, answers: newAnswers });
        scrollToRoot(); //scroll back to finder-top
    }, [state.history, state.answers]);

    const jumpTo = useCallback((frageId) => {
        const idx = state.history.findIndex(q => q.id === frageId);
        if (idx === -1) return;

        const newHistory = state.history.slice(0, idx + 1);
        const newAnswers = {};
        newHistory.forEach(q => { if (state.answers[q.id]) newAnswers[q.id] = state.answers[q.id]; });
        dispatch({ type: 'BACK', history: newHistory, answers: newAnswers });
    }, [state.history, state.answers]);

    /* ---------- Render ---------- */
    return (
        <>
        {loadedFinder && (
            <div id="productfinder-root">
                {/*
                <div id="productfinder-root" class="border alert alert-light shadow-lg mb-5">
                <h2>{state.headline}</h2>


                        {!showFinder && (
                            <>
                            <p>
                        {state.description}
                        </p>

                    <p class="text-center">
                        <button
                            className="btn btn-success shadow-lg border-secondary"
                            onClick={() => setShowFinder(true)}
                        >
                            <i className="fa fa-star-o fa-fw" aria-hidden="true"></i> Jetzt das passende Produkt
                            finden! <i className="fa fa-star-o fa-fw" aria-hidden="true"></i>
                        </button>
                    </p>
                </>
                )}
                */}

                {showFinder && (
                    <div>
                        {/*<div className="rounded alert-secondary my-4 p-4">*/}
                        {/* Fehleranzeige */}
                        {state.error && (
                            <div className="alert alert-danger">
                                {state.error}
                            </div>
                        )}

                        {/* Ladeanzeige */}
                        {state.loading && !state.question && state.products.length === 0 && (
                            <div className="text-muted">Lade...</div>
                        )}

                        {/* Frage oder Produktansicht */}
                        <>

                            {state.history.length > 1 && (
                                <SelectionSummary
                                    history={state.history}
                                    answers={state.answers}
                                    onJump={jumpTo}
                                />
                            )}

                            {/* Fragenanzeige */}
                            {state.question && (
                                <>
                                    <QuestionView
                                        question={state.question}
                                        givenAnswer={state.answers[state.question.id]}
                                        onAnswer={handleAnswer}
                                        questionNumber={state.answered + 1}
                                    />
                                </>
                            )}

                            {/* Produktanzeige */}
                            {state.products.length > 0 && (
                                <>
                                    <ProductList products={state.products} />
                                </>
                            )}

                            {state.history.length > 1 && (
                                <div className="my-3 text-center">
                                    <button className="btn btn-secondary" onClick={backOneStep}>
                                        Zurück
                                    </button>
                                </div>
                            )}
                        </>
                    </div>
                )}
            </div>
        )}
        </>
    );
};

export default ProductFinder;